import { useEffect, useRef } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import { useXR } from "@react-three/xr";
import { createPhysicalFloorLock, floorCandidate } from "./physicalFloor";

export default function XrFloorRecovery() {
  const { gl, scene } = useThree();
  const player = useXR(state => state.player);
  const session = useXR(state => state.session);
  const isPresenting = useXR(state => state.isPresenting);
  const alignment = useRef(null);

  useEffect(() => {
    if (!session || !player || !isPresenting) return;
    const state = { policy: createPhysicalFloorLock(), reference: null, floor: null };
    state.reset = () => { state.policy.reset(); state.floor = null; };
    alignment.current = state;
    scene.userData.floorAlignment = { status: "scanning", floorY: null };
    return () => {
      state.reference?.removeEventListener?.("reset", state.reset);
      alignment.current = null;
      delete scene.userData.floorAlignment;
      player.position.y = 0;
      player.updateMatrixWorld(true);
    };
  }, [session, player, isPresenting, scene]);

  useFrame((_, __, frame) => {
    const state = alignment.current;
    if (!state || !frame || session.visibilityState === "hidden") return;
    const reference = gl.xr.getReferenceSpace();
    if (!reference) return;
    if (reference !== state.reference) {
      state.reference?.removeEventListener?.("reset", state.reset);
      state.reference = reference;
      reference.addEventListener?.("reset", state.reset);
      state.reset();
    }
    const viewer = frame.getViewerPose(reference);
    if (state.floor === null && viewer && !viewer.emulatedPosition) {
      let candidate = null;
      for (const plane of frame.detectedPlanes || []) {
        const next = floorCandidate(plane, frame.getPose(plane.planeSpace, reference), viewer.transform.position.y);
        if (next && (!candidate || next.area > candidate.area)) candidate = next;
      }
      state.floor = state.policy.sample(candidate, performance.now());
    }
    scene.userData.floorAlignment = {
      status: state.floor !== null ? "aligned" : "scanning",
      floorY: state.floor,
      planeCount: frame.detectedPlanes?.size || 0,
    };
    // Map the measured physical floor to the scene's y=0 plane. Move the entire
    // rig so hands, controllers, the car, and its shadow retain their relationship.
    player.position.y = 0 - (state.floor ?? 0);
    player.updateMatrixWorld(true);
  }, -100);

  return null;
}
